import { makeStyles } from "tss-react/mui";
import { LinkedExtended } from "@miplatform/assets";
import { MiButton } from "@miplatform/components";
import { BUTTON_COLORS, OPTION_STATUS } from "@miplatform/constants";
import {
  IBidOptionMaterial,
  IMaterialsResponse,
  IMergedBidArea,
} from "@miplatform/types";
import { mdiArrowULeftTop, mdiTrashCanOutline } from "@mdi/js";
import { QuantityField } from "@mibuildweb/components";
import { Box, Collapse, Divider, useTheme } from "@mui/material";
import { ThinMaterialCard } from "../../../../../components/MaterialCard/ThinMaterialCard";

export function BidMaterialOption({
  item,
  areaMaterial,
  area,
  isLast,
  isDisabled,
  showLinkedItems,
  linkedMaterialDetails,
  isAreaRemoved,
  isAreaExisting,
  isEditingOption,
  menuData = [],
  handleRemoveItemFromArea,
  handleReturnItemToPlan,
  handleUpdateItemQuantity,
}: Readonly<BidMaterialOptionProps>) {
  const theme = useTheme();

  const materialStatus = areaMaterial.optionStatus;
  const isMaterialRemoved = materialStatus === OPTION_STATUS.REMOVED;
  const isMaterialAdded = materialStatus === OPTION_STATUS.ADDED;
  const isCardRemoved = isAreaRemoved || isMaterialRemoved;
  const isQtyEditable = isEditingOption && isMaterialAdded;

  const getBgColor = () => {
    if (isMaterialAdded && isAreaExisting) {
      return theme.palette.green.light;
    }
    if (isMaterialRemoved && !isAreaRemoved) {
      return theme.palette.red.light;
    }
    return undefined;
  };

  const { classes } = useStyles({ bgColor: getBgColor() });

  const getItemAction = () => {
    if (isAreaRemoved) return null;
    if (isMaterialRemoved) {
      return (
        <MiButton
          onClick={() => handleReturnItemToPlan(area, areaMaterial)}
          disabled={isDisabled}
          color={BUTTON_COLORS.PRIMARY}
          startIcon={{ path: mdiArrowULeftTop }}
          size="small"
        />
      );
    }
    return (
      <MiButton
        onClick={() => handleRemoveItemFromArea(area, areaMaterial)}
        disabled={isDisabled}
        color={BUTTON_COLORS.ERROR}
        startIcon={{ path: mdiTrashCanOutline }}
        size="small"
      />
    );
  };

  return (
    <Box>
      <Box className={classes.flexRow}>
        {isEditingOption && getItemAction()}
        <ThinMaterialCard
          item={item}
          isSuppliedByBuilder={areaMaterial?.suppliedByBuilder}
          isRemoved={isCardRemoved}
          showSalePrice
          cardStyle={{
            padding: theme.spacing(0, 2, 0, 0),
            borderBottom: "none",
            height: 28,
          }}
          leftAction={
            <QuantityField
              quantity={areaMaterial?.quantity}
              disabled={!isQtyEditable}
              onChange={(qty) =>
                handleUpdateItemQuantity(area, areaMaterial.materialId, qty)
              }
            />
          }
          menuData={menuData}
        />
      </Box>
      {item?.linkedMaterials?.map((linked) => {
        const linkedDetail = linkedMaterialDetails.find(
          (m) => m.id === linked.materialId
        );

        return (
          <Collapse in={showLinkedItems} key={linked.materialId} unmountOnExit>
            <Box className={classes.flexRow}>
              {isEditingOption && !isAreaRemoved && <Box width={33} />}
              <Box className={classes.linkedItemBox}>
                <img
                  src={LinkedExtended}
                  alt="linkExtendedImage"
                  className={classes.linkedExtendedImg}
                />
                <ThinMaterialCard
                  item={linkedDetail}
                  isRemoved={isCardRemoved}
                  showSalePrice
                  cardStyle={{
                    padding: theme.spacing(0, 2, 0, 0),
                    borderBottom: "none",
                  }}
                  leftAction={
                    <QuantityField
                      quantity={linked.quantity * (areaMaterial?.quantity || 0)}
                      disabled
                    />
                  }
                />
              </Box>
            </Box>
          </Collapse>
        );
      })}
      {!isLast && <Divider className={classes.divider} />}
    </Box>
  );
}

const useStyles = makeStyles<{ bgColor?: string }>()((theme, { bgColor }) => ({
  flexRow: {
    display: "flex",
    alignItems: "center",
    backgroundColor: bgColor,
  },
  divider: {
    width: "100%",
  },
  linkedItemBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    height: 28,
  },
  linkedExtendedImg: {
    position: "relative",
    bottom: 16,
    left: 9,
  },
}));

interface BidMaterialOptionProps {
  item: IMaterialsResponse;
  areaMaterial: IBidOptionMaterial;
  area: IMergedBidArea;
  isLast: boolean;
  isDisabled: boolean;
  showLinkedItems: boolean;
  linkedMaterialDetails: IMaterialsResponse[];
  isAreaRemoved: boolean;
  isAreaExisting: boolean;
  isEditingOption: boolean;
  menuData?: { label: string; icon: string; color?: string; action: () => void }[];
  handleRemoveItemFromArea: (
    area: IMergedBidArea,
    areaMaterial: IBidOptionMaterial
  ) => void;
  handleReturnItemToPlan: (
    area: IMergedBidArea,
    areaMaterial: IBidOptionMaterial
  ) => void;
  handleUpdateItemQuantity: (
    area: IMergedBidArea,
    materialId: string,
    quantity: number
  ) => void;
}
